/**
 * NewAgentArea — ADR-111 IA-9: creating an agent is an AREA with its own route
 * (/fleet-editor/agents/new), not a panel stacked over whichever agent happened
 * to be selected. It creates a brand-new agent profile, a scaffolded SOUL.md,
 * and optional skill stubs via POST /api/agents/propose (fleet_owner).
 *
 * Two exported surfaces: NewAgentForm (the area itself, built from NewAgentShell
 * and ProposedState) and NewAgentProposeStrip — the receipt the Fleet Editor
 * shell renders when the operator walks away from a propose still in flight.
 *
 * Core agents (ADR-103) are excluded from this authoring surface by construction
 * (there is no picker). Every change is a PR + in-app improvement — nothing runs
 * or stops until it's approved.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { api, AuthorTestsFailingError } from '../api/client'
import type { AgentProposeSkillInput, FailingCheck, ProposeSkillResult } from '../api/client'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { ProposeResultBanner } from '../components/ProposeResultBanner'
import { DEFAULT_MODEL, DEFAULT_PROVIDER } from './agentModels'
import { ModelPicker, useModelCatalog } from '../components/ModelPicker'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

const PROFILE_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/

/**
 * A propose whose HTTP request is still in flight when its form goes away.
 * `profile` is captured at send time so the receipt can name the agent even
 * though the form that typed it is gone.
 */
export type PendingPropose = { profile: string; promise: Promise<ProposeSkillResult> }

/**
 * How long the receipt waits for a propose to settle before it lets the
 * operator dismiss an unfinished one. api.proposeAgent is a plain fetch with no
 * timeout and no abort, so a request that hangs never settles either way — and
 * because dismissal is what lifts the Propose gate, a hung request would
 * otherwise leave the operator unable to propose ANY agent until they reloaded
 * the page. Three minutes is well past any legitimate propose: the call is
 * synchronous, so it is already bounded by Cloudflare's 100s edge ceiling
 * (see convertSkill in api/client.ts). Anything still running here is stuck.
 */
const STALL_MS = 180_000

/**
 * The receipt for a propose the operator walked away from mid-flight.
 *
 * Leaving the create area (a rail agent, Resources/Data, ✕ close, browser Back)
 * unmounts NewAgentForm while the PR is already being opened. Without this the
 * resolved result lands on nothing: the operator never sees the PR number, the
 * improvement link, or — worse — a failure, having already done the one thing
 * that cannot be undone. NewAgentForm hands the promise to the Fleet Editor
 * shell on its way out, and the shell renders this above whatever the operator
 * moved on to. It survives every navigation inside the section, because the
 * shell is the route element for all of them.
 */
export function NewAgentProposeStrip({ pending, onDismiss }: { pending: PendingPropose; onDismiss: () => void }) {
  const [result, setResult] = useState<ProposeSkillResult | null>(null)
  // The rejection is kept AS THROWN, not stringified: AuthorTestsFailingError
  // carries the per-check detail FailingChecksPanel renders, and the operator
  // who walked away needs it exactly as much as the one who stayed.
  const [err, setErr] = useState<unknown>(null)
  // Set once the request has gone STALL_MS with no answer — see STALL_MS.
  const [stalled, setStalled] = useState(false)
  useEffect(() => {
    let live = true
    // A different propose is a different receipt. Without this reset the strip
    // renders the NEW agent's name over the PREVIOUS one's PR link — a receipt
    // for an irreversible action attached to the wrong agent. The shell also
    // keys this component on the profile, so the usual case is a remount; this
    // covers the same name proposed twice, where the key does not change.
    setResult(null); setErr(null); setStalled(false)
    const stall = setTimeout(() => { if (live) setStalled(true) }, STALL_MS)
    pending.promise.then(
      r => { if (live) setResult(r) },
      e => { if (live) setErr(e) },
    )
    return () => { live = false; clearTimeout(stall) }
  }, [pending])
  // Dismiss CLOSES the receipt and, with it, the Propose gate the shell derives
  // from this pending. While the request is still running that combination is
  // the duplicate-PR path: the operator hides an unfinished propose, walks back
  // into a blank form with Propose armed, and sends the same agent twice. So
  // dismissal waits for the request to settle — the point at which there is
  // something to have read. A request that never answers at all is the one
  // exception (STALL_MS): the strip says so and reopens dismiss, because
  // otherwise a hung fetch strands the operator with Propose closed for good.
  const settled = result !== null || err !== null
  const dismissable = settled || stalled
  return (
    <div data-testid="new-agent-propose-strip"
      className="mb-3 shrink-0 rounded border border-gray-200 bg-white px-3 py-2 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-gray-600">
          <span className="font-mono">{pending.profile}</span>
          {result ? ' — proposed while you moved on.'
            : err ? ' — its propose failed after you left.'
            : stalled ? ' — no response yet. Check Improvements before proposing it again.'
            : ' — still proposing…'}
        </p>
        <button type="button" onClick={onDismiss} disabled={!dismissable} aria-label="Dismiss propose receipt"
          title={settled ? 'Dismiss'
            : stalled ? 'Dismiss — the request never answered; check Improvements before proposing again'
            : 'Still proposing — this clears once the request finishes'}
          className="text-xs text-gray-400 hover:text-gray-700 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed">✕</button>
      </div>
      {result && <ProposeResultBanner result={result} />}
      {err !== null && (err instanceof AuthorTestsFailingError
        ? <FailingChecksPanel checks={err.checks} />
        : <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{em(err)}</p>)}
    </div>
  )
}

/**
 * The create-an-agent AREA's chrome (ADR-111 IA-9). It is the whole main pane at
 * /fleet-editor/agents/new, not a panel layered over an agent, so it carries a
 * page-level heading like the Resources and Data areas beside it rather than the
 * card it used to be.
 */
function NewAgentShell({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">New agent</h1>
          <p className="text-sm text-gray-500 mt-1">
            Author a brand-new agent. Proposing sends it for review — nothing is applied
            straight away.
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-800 shrink-0 pt-1">✕ close</button>
      </div>
      {children}
    </div>
  )
}

/** The terminal state a successful propose lands in — see NewAgentForm. */
function ProposedState({ result, profile, onAuthorAnother, onDone }: {
  result: ProposeSkillResult
  profile: string
  onAuthorAnother: () => void
  onDone: () => void
}) {
  return (
    <div className="space-y-3" data-testid="new-agent-proposed">
      <ProposeResultBanner result={result} />
      <p className="text-xs text-gray-600">
        <span className="font-mono">{profile}</span> joins the Agents rail once this
        change is approved and deployed — it isn't running yet.
      </p>
      <div className="flex items-center gap-2">
        <button type="button" onClick={onAuthorAnother}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
          Author another
        </button>
        <button type="button" onClick={onDone}
          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">
          Done
        </button>
      </div>
    </div>
  )
}

/** NewAgentForm — the Agents rail's "＋ New agent" flow.
 *
 * `entryKey` changes every time the operator navigates INTO the create area
 * (the shell passes the router's location key). See the effect below: it is
 * how a finished propose is cleared on re-entry WITHOUT remounting the form. */
export function NewAgentForm({
  canPropose,
  onClose,
  entryKey,
  onLeaveWhileBusy,
  pendingProfile,
}: {
  canPropose: boolean
  onClose: () => void
  entryKey: string
  /** Called on unmount while a propose is still in flight — see PendingPropose. */
  onLeaveWhileBusy: (pending: PendingPropose) => void
  /**
   * The profile of a handed-off propose whose receipt is still on screen, or
   * null. Propose is closed while one is outstanding: re-entering the area
   * mounts a blank form, and nothing else stops the operator retyping the same
   * agent and opening a SECOND PR for it. Dismissing the receipt reopens it.
   */
  pendingProfile: string | null
}) {
  const [profile, setProfile] = useState('')
  const [model, setModel] = useState(DEFAULT_MODEL)
  // ADR-182 D3: created with an explicit (provider, model) pair, so a new agent
  // is born self-describing rather than relying on an implied vendor.
  const [provider, setProvider] = useState(DEFAULT_PROVIDER)
  const [role, setRole] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [skillRows, setSkillRows] = useState<{ name: string; description: string }[]>([])
  const { catalog, catalogError, catalogLoading } = useModelCatalog()

  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [result, setResult] = useState<ProposeSkillResult | null>(null)
  // The name as SENT. The fields stay editable while the request runs (only
  // Propose is disabled), so reading the live field after the await would name
  // whatever was typed in the meantime as the agent that was proposed.
  const [proposedProfile, setProposedProfile] = useState('')

  const createValid = PROFILE_RE.test(profile.trim())

  const addSkillRow = () => setSkillRows(rows => [...rows, { name: '', description: '' }])
  const updateSkillRow = (i: number, field: 'name' | 'description', value: string) =>
    setSkillRows(rows => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)))
  const removeSkillRow = (i: number) => setSkillRows(rows => rows.filter((_, idx) => idx !== i))

  /** Back to a blank form, for authoring a second agent without leaving. */
  const authorAnother = useCallback(() => {
    setProfile(''); setModel(DEFAULT_MODEL); setProvider(DEFAULT_PROVIDER); setRole(''); setEnabled(true)
    setSkillRows([]); setNote(''); setErr(null); setFailingChecks([]); setResult(null)
    setProposedProfile('')
  }, [])

  // Re-entering the area — clicking either "＋ New agent" entry while it is
  // ALREADY open — must clear a FINISHED propose: that state is terminal, both
  // entries point at the route that is already active, so without this they are
  // dead controls whose only escape is the "Author another" button.
  //
  // It deliberately clears NOTHING ELSE. Remounting the whole form on re-entry
  // would also do the job, but it would throw away a half-filled draft on a
  // click the operator may well have made by accident (the rail row is the
  // highlighted one while you are here), and it would orphan an in-flight
  // propose — the request still opens a PR, the resolved result lands on an
  // unmounted component, and the operator, seeing a blank form, proposes the
  // same agent twice. Keeping the form mounted keeps `busy` and the pending
  // result attached to it.
  //
  // Deps are entryKey ALONE: `result` is read, not depended on — reacting to it
  // would reset the form the instant a propose succeeded, which is the terminal
  // state this is meant to leave standing until the operator navigates back in.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (result) authorAnother() }, [entryKey])

  // Leaving the area mid-propose is the one exit this form cannot make safe on
  // its own: the request is already opening a PR and this component is about to
  // stop existing. Hand the in-flight promise up so the shell can show the
  // receipt wherever the operator went. Read through a ref so the unmount
  // cleanup sees the latest callback without re-subscribing (and firing) on
  // every prop identity change.
  const pending = useRef<PendingPropose | null>(null)
  const onLeave = useRef(onLeaveWhileBusy)
  useEffect(() => { onLeave.current = onLeaveWhileBusy })
  useEffect(() => () => { if (pending.current) onLeave.current(pending.current) }, [])

  const propose = async () => {
    setBusy(true); setErr(null); setFailingChecks([])
    try {
      const skills: AgentProposeSkillInput[] = skillRows
        .filter(r => r.name.trim() !== '')
        .map(r => ({ name: r.name.trim(), description: r.description.trim() || undefined }))
      const sent = profile.trim()
      const request = api.proposeAgent({
        profile: sent,
        mode: 'create',
        model,
        provider,
        role_description: role.trim() || undefined,
        enabled,
        skills,
        message: note.trim() || undefined,
      })
      pending.current = { profile: sent, promise: request }
      const r = await request
      setProposedProfile(sent)
      setResult(r)
      // Deliberately no roster refresh: GET /api/agents is ListAgents(fleetYAML),
      // reading the DEPLOYED config/fleet.yaml on disk, while a propose writes to
      // develop through GitHub. The new agent cannot appear in the rail until the
      // change is approved and deployed — which is what the result banner says.
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else setErr(em(e))
    } finally {
      pending.current = null
      setBusy(false)
    }
  }

  // A successful propose ENDS this form. Leaving the fields up reads as "the
  // agent exists now", which is wrong: the roster comes from the deployed
  // config/fleet.yaml and a propose writes to develop, so nothing changes until
  // the change is approved and deployed. Say that instead.
  if (result) {
    return (
      <NewAgentShell onClose={onClose}>
        <ProposedState result={result} profile={proposedProfile} onAuthorAnother={authorAnother} onDone={onClose} />
      </NewAgentShell>
    )
  }

  return (
    <NewAgentShell onClose={onClose}>
      <label className="text-xs text-gray-600 block">Profile name (lowercase, hyphens)
        <input type="text" value={profile} onChange={e => setProfile(e.target.value)}
          placeholder="e.g. deals-analyst"
          className="mt-1 block w-full text-sm font-mono border border-gray-300 rounded px-2 py-1" />
        {profile.trim() !== '' && !createValid && <span className="text-[11px] text-red-600">lowercase letters, digits, and hyphens only</span>}
      </label>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-600 block">Model
          <ModelPicker
            value={{ model, provider }}
            onChange={next => { setModel(next.model); setProvider(next.provider) }}
            catalog={catalog}
            loading={catalogLoading}
          />
        </label>
        <label className="text-xs text-gray-600 flex items-center gap-1.5 pb-1.5">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Enabled
        </label>
      </div>
      <label className="text-xs text-gray-600 block">Role description
        <input type="text" value={role} onChange={e => setRole(e.target.value)}
          placeholder="e.g. analyzes deal pipelines"
          className="mt-1 block w-full text-sm border border-gray-300 rounded px-2 py-1" />
      </label>
      <div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">Skill stubs (optional)</span>
          <button type="button" onClick={addSkillRow} className="text-[11px] px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">＋ Add skill</button>
        </div>
        {skillRows.map((row, i) => (
          <div key={i} className="mt-1 flex items-center gap-1.5">
            <input type="text" value={row.name} onChange={e => updateSkillRow(i, 'name', e.target.value)}
              placeholder="skill name" className="text-sm border border-gray-300 rounded px-2 py-1 w-40" />
            <input type="text" value={row.description} onChange={e => updateSkillRow(i, 'description', e.target.value)}
              placeholder="description" className="flex-1 text-sm border border-gray-300 rounded px-2 py-1" />
            <button type="button" onClick={() => removeSkillRow(i)} className="text-red-600 hover:text-red-800 text-xs">✕</button>
          </div>
        ))}
      </div>

      {/* A catalog that failed to load leaves an EMPTY picker. Saying so beats
          an operator staring at a control with no options and concluding the
          feature is broken in some other way. */}
      {catalogError && (
        <p className="text-xs text-red-600 whitespace-pre-wrap break-words">
          Couldn't load the model catalog ({catalogError}) — no model can be chosen
          until this loads.
        </p>
      )}
      {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{err}</p>}
      {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}

      {pendingProfile && (
        <p className="text-xs text-gray-500">
          <span className="font-mono">{pendingProfile}</span> is already being proposed — its
          receipt is above. You can author another agent once that receipt is dismissed.
        </p>
      )}

      <div className="flex items-center gap-2">
        {canPropose ? (
          <button type="button" disabled={busy || !createValid || !!pendingProfile} onClick={() => void propose()}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
            {busy ? 'Proposing…' : 'Propose new agent'}
          </button>
        ) : <span className="text-xs text-gray-500">Only a fleet owner can propose a new agent.</span>}
        <input type="text" value={note} onChange={e => setNote(e.target.value)}
          placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
      </div>
    </NewAgentShell>
  )
}
